const asRows = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const isEndOfDaySummaryData = (value) =>
  value && typeof value === "object" && !Array.isArray(value) &&
  ["sales", "creditRedemptions", "appointmentDeposit", "deposits", "refunds"].every(
    (key) => value[key] && typeof value[key] === "object" && !Array.isArray(value[key]),
  );

const numberValue = (value, fallback = 0) => {
  if (value == null || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const nullableNumber = (value) => (value == null || value === "" ? null : numberValue(value, null));

const firstDefined = (row, keys, fallback = undefined) => {
  for (const key of keys) {
    if (row && row[key] != null && row[key] !== "") return row[key];
  }
  return fallback;
};

const firstNumber = (row, keys, fallback = null) => {
  const value = firstDefined(row, keys);
  return value === undefined ? fallback : nullableNumber(value);
};

const accountingAmounts = (row) => ({
  receipts_received: firstNumber(row, ["receipts_received"]),
  refunds: firstNumber(row, ["refunds"]),
  receipt_count: firstNumber(row, ["receipt_count"]),
  invoiced_sales: firstNumber(row, ["invoiced_sales"]),
  payments_collected: firstNumber(row, ["payments_collected"]),
  net_sales: firstNumber(row, ["net_sales", "net_sale", "TotalNetSale", "total_net_sale"]),
  tax: firstNumber(row, ["tax", "TotalTax", "total_tax"]),
  gross_sale: firstNumber(row, ["gross_sale", "gross_sales", "TotalGrossSale", "total_gross_sale"]),
});

const activityValue = (row, accounting) =>
  firstNumber(row, ["cart_line_cost_total", "activity_value"], accounting.net_sales ?? 0);

const pageValue = (source, pagination, key, fallback) => {
  if (source && source[key] != null) return source[key];
  if (pagination && pagination[key] != null) return pagination[key];
  return fallback;
};

export const safeCapitalizedName = (value) => {
  const text = value == null ? "" : String(value);
  return text.length ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

export const normalizeReportListResponse = (payload) => {
  const source =
    payload &&
    typeof payload === "object" &&
    payload.data &&
    !Array.isArray(payload.data) &&
    Object.prototype.hasOwnProperty.call(payload.data, "data")
      ? payload.data
      : payload;
  const safeSource = source && typeof source === "object" ? source : {};
  const paginationValue = safeSource.pagination || safeSource.meta;
  const pagination = paginationValue && typeof paginationValue === "object" ? paginationValue : {};
  const preserveEndOfDaySummary = safeSource.ScreenReport === "sales_by_end_of_day" && isEndOfDaySummaryData(safeSource.data);
  const rows = preserveEndOfDaySummary
    ? safeSource.data
    : Array.isArray(source)
      ? source
      : asRows(safeSource.data);
  const currentPage = Math.max(1, numberValue(pageValue(safeSource, pagination, "current_page", pageValue(safeSource, pagination, "page", 1)), 1));
  // Preserve the server's last page even when a caller deliberately probes a
  // page beyond the end. Inflating it to the requested page hides that bound.
  const lastPage = Math.max(1, numberValue(pageValue(safeSource, pagination, "last_page", pageValue(safeSource, pagination, "pages", currentPage)), currentPage));
  const perPage = Math.max(1, numberValue(pageValue(safeSource, pagination, "per_page", rows.length || 25), rows.length || 25));
  const total = Math.max(0, numberValue(pageValue(safeSource, pagination, "total", rows.length), rows.length));
  const suppliedNextPage = pageValue(safeSource, pagination, "next_page_url", undefined);
  const nextPageUrl = suppliedNextPage !== undefined
    ? suppliedNextPage
    : currentPage < lastPage
      ? `afterlogin/report/view?page=${currentPage + 1}`
      : null;

  return {
    ...(!Array.isArray(source) ? safeSource : {}),
    current_page: currentPage,
    last_page: lastPage,
    next_page_url: nextPageUrl,
    data: rows,
    per_page: perPage,
    total,
    from: pageValue(safeSource, pagination, "from", total > 0 ? (currentPage - 1) * perPage + 1 : 0),
    to: pageValue(safeSource, pagination, "to", total > 0 ? Math.min(currentPage * perPage, total) : 0),
  };
};

export const normalizeSalesByDayRow = (row = {}) => {
  const amounts = accountingAmounts(row);
  return {
    id: firstDefined(row, ["id", "date", "day"]),
    day: firstDefined(row, ["day", "date"], ""),
    receipts_received: firstNumber(row, ['receipts_received']),
    refunds: firstNumber(row, ['refunds']),
    receipt_count: firstNumber(row, ['receipt_count']),
    item_sold: numberValue(firstDefined(row, ["item_sold", "TotalItemSold"])),
    service_item_sold: numberValue(firstDefined(row, ["service_item_sold", "TotalServiceSold"])),
    product_item_sold: numberValue(firstDefined(row, ["product_item_sold", "TotalProductSold"])),
    discount_total: numberValue(firstDefined(row, ["discount_total", "TotalDiscount"])),
    activity_value: activityValue(row, amounts),
    ...amounts,
  };
};

export const normalizeSalesByDayRows = (value) => asRows(value).map(normalizeSalesByDayRow);

export const normalizeSalesByTypeRow = (row = {}) => {
  const amounts = accountingAmounts(row);
  return {
    id: firstDefined(row, ["id", "type"]),
    type: firstDefined(row, ["type", "name"], ""),
    item_sold: firstNumber(row, ["item_sold", "TotalItemSold"]),
    activity_value: activityValue(row, amounts),
    ...amounts,
  };
};

export const normalizeSalesByTypeRows = (value) => asRows(value).map(normalizeSalesByTypeRow);

export const normalizeSalesByServiceRow = (row = {}) => {
  const amounts = accountingAmounts(row);
  return {
    id: row.id,
    name: firstDefined(row, ["name", "service_name"], ""),
    item_sold: firstNumber(row, ["item_sold", "TotalItemSold"]),
    activity_value: activityValue(row, amounts),
    ...amounts,
  };
};

export const normalizeSalesByServiceRows = (value) => asRows(value).map(normalizeSalesByServiceRow);

export const normalizeSalesByProductRow = (row = {}) => {
  const amounts = accountingAmounts(row);
  return {
    id: row.id,
    name: firstDefined(row, ["name", "product_name"], ""),
    staff_name: firstDefined(row, ["staff_name"], ""),
    supplier_name: firstDefined(row, ["supplier_name"], row.supplier && row.supplier.name) || "",
    total_cost_value: firstNumber(row, ["total_cost_value", "TotalCostValue"]),
    total_retail_value: firstNumber(row, ["total_retail_value", "TotalRetailValue"]),
    cost_price: firstNumber(row, ["cost_price", "cost"]),
    retail_price: firstNumber(row, ["retail_price", "price"]),
    item_sold: firstNumber(row, ["item_sold", "TotalItemSold", "cart_line_count"]),
    quantity_total: numberValue(firstDefined(row, ["quantity_total", "quantity", "TotalQuantity"])),
    activity_value: activityValue(row, amounts),
    ...amounts,
  };
};

export const normalizeSalesByProductRows = (value) => asRows(value).map(normalizeSalesByProductRow);

const splitDisplayName = (value) => {
  const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { first_name: "", last_name: "" };
  if (parts.length === 1) return { first_name: parts[0], last_name: "" };
  return { first_name: parts.slice(0, -1).join(" "), last_name: parts[parts.length - 1] };
};

export const normalizeSalesByStaffRow = (row = {}) => {
  const amounts = accountingAmounts(row);
  const explicitFirst = firstDefined(row, ["first_name", "firstName"]);
  const explicitLast = firstDefined(row, ["last_name", "lastName"]);
  const displayName = splitDisplayName(row.name);
  return {
    id: row.id,
    first_name: String(explicitFirst ?? displayName.first_name ?? ""),
    last_name: String(explicitLast ?? displayName.last_name ?? ""),
    services_sold: firstNumber(row, ["service_cart_line_count", "TotalServiceSold", "total_service_sold", "services_sold", "total_services"]),
    products_sold: firstNumber(row, ["product_cart_line_count", "TotalProductSold", "total_product_sold", "products_sold", "total_products"]),
    package_sales: numberValue(row.package_sales),
    subscription_sales: numberValue(row.subscription_sales),
    packages_sold: firstNumber(row, ["package_cart_line_count", "packages_sold"]),
    subscriptions_sold: firstNumber(row, ["subscription_cart_line_count", "subscriptions_sold"]),
    activity_value: firstNumber(row, ["service_cart_line_cost_total", "cart_line_cost_total", "activity_value"], amounts.net_sales ?? 0),
    ...amounts,
  };
};

export const normalizeSalesByStaffRows = (value) => asRows(value).map(normalizeSalesByStaffRow);

export const normalizeGiftVoucherActivityRow = (row = {}) => {
  const value = numberValue(firstDefined(row, ["total_value_purchased", "TotalValue", "total_value", "value"]));
  const explicitRemaining = firstDefined(row, ["remaining_value", "remaining"]);
  const explicitRedeemed = firstDefined(row, ["redeemed_value", "TotalValueRedeemed", "total_value_redeemed", "value_redeemed", "redeemed"]);
  const remaining = explicitRemaining === undefined ? Math.max(0, value - numberValue(explicitRedeemed)) : numberValue(explicitRemaining);
  const redeemed = explicitRedeemed === undefined ? Math.max(0, value - remaining) : numberValue(explicitRedeemed);
  return {
    id: row.id,
    name: String(firstDefined(row, ["name", "voucher_name", "gift_voucher"], "") || ""),
    quantity: numberValue(firstDefined(row, ["quantity_purchased", "TotalQuantity", "total_quantity", "quantity"])),
    value,
    redeemed,
    remaining,
    net: numberValue(firstDefined(row, ["net", "NetAmount", "net_amount"]), remaining),
  };
};

export const normalizeGiftVoucherActivityRows = (value) => asRows(value).map(normalizeGiftVoucherActivityRow);

export const normalizeEndOfDayRows = (value) =>
  asRows(value).map((row = {}) => ({
    payment_reference: row.payment_reference,
    sale_reference: row.sale_reference,
    client_reference: row.client_reference ?? null,
    payment_at: row.payment_at ?? null,
    payment_type: row.payment_type ?? null,
    paid_by: row.paid_by ?? null,
    status: row.status ?? null,
    amount: nullableNumber(row.amount),
    currency: row.currency ?? null,
    is_deposit: Boolean(row.is_deposit),
    is_booking_widget: Boolean(row.is_booking_widget),
  }));

export const normalizeCustomPaymentOptionBreakdown = (value) =>
  asRows(value)
    .map((row = {}) => ({
      payment_option_id: numberValue(row.payment_option_id, null),
      name: typeof row.name === "string" ? row.name.trim() : "",
      transaction_count: numberValue(row.transaction_count, 0),
      amount: nullableNumber(row.amount),
      currency: typeof row.currency === "string" ? row.currency : null,
    }))
    .filter((row) => row.payment_option_id != null && row.name !== "");

export const normalizeClientCartProductRows = (value) => {
  const rows = value && typeof value === "object" && !Array.isArray(value) && value.data != null
    ? asRows(value.data)
    : asRows(value);
  return rows.map((row = {}) => {
    const product = row.product && typeof row.product === "object" ? row.product : row;
    return {
      id: product.id ?? row.product_id ?? row.id,
      name: firstDefined(product, ["name", "product_name"], "") || "",
      image_url: firstDefined(product, ["image_url", "image", "url"], null),
      cart_line_id: row.cart_line_id ?? row.id,
    };
  });
};

export { asRows, numberValue };
